import { useState } from 'react';

import StarIcon from '@mui/icons-material/Star';
import { Box, Button, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';

import { Review, ReviewWithSummary } from '@src/lib/__generated/sdk';
import { getTop3ReviewsWithComments } from '@src/util/review-with-comments';

import {
	BannerLinearProgress,
	bannerButtonStyles,
	featuredProviderReviewsStyles,
	getProviderContentContainerStyles,
} from './ProviderDetailsPage.styles';

interface FeaturedProviderReviewsProps {
	reviews?: ReviewWithSummary;
	selectedReviews?: string[] | null;
	title?: string;
	providerName?: string;
}

export default function FeaturedProviderReviews({
	reviews,
	selectedReviews,
	title,
	providerName,
}: Readonly<FeaturedProviderReviewsProps>) {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [expandedReviews, setExpandedReviews] = useState<Record<string, boolean>>({});

	if (!reviews?.reviews?.length || (reviews.reviewCount ?? 0) < 5) return null;

	const featuredReviews = getTop3ReviewsWithComments(selectedReviews, reviews);
	const totalReviews = reviews.reviewCount ?? 0;

	const sortedRatings = [5, 4, 3, 2, 1].map((rating) => {
		const existing = reviews.ratings?.find((r) => r?.rating === rating);
		return existing ?? { rating, reviewCount: 0 };
	});

	const toggleExpand = (reviewId: string) => {
		setExpandedReviews((prev) => ({ ...prev, [reviewId]: !prev[reviewId] }));
	};

	const bannerStarCount = Math.max(0, Math.min(5, Math.floor(Number(reviews.averageRating ?? 0)) || 0));

	return (
		<Box sx={getProviderContentContainerStyles} data-testid="featured_provider_reviews_container">
			<Box sx={featuredProviderReviewsStyles.container}>
				{title && (
					<Typography variant="header1" component="h2" color="text.primary">
						{title}
					</Typography>
				)}

				{/* Gradient banner */}
				<Box sx={featuredProviderReviewsStyles.banner}>
					{/* Left: large rating + stars + review count */}
					<Box sx={featuredProviderReviewsStyles.ratingDisplay}>
						<Typography
							component="p"
							sx={{
								fontFamily: 'Tiempos Headline',
								fontSize: { xs: '2.5rem', md: '3.3125rem' },
								fontWeight: 500,
								fontStyle: 'normal',
								color: 'white',
								lineHeight: '68px',
								letterSpacing: '-0.5px',
							}}
							data-testid="average_rating"
						>
							{reviews.averageRating == null ? '0' : Number(reviews.averageRating).toFixed(2)} out of 5
						</Typography>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '0.125rem', flexWrap: 'wrap' }}>
							{Array.from({ length: bannerStarCount }).map((_, i) => (
								<StarIcon key={i} sx={{ color: '#F5C518', fontSize: 22 }} />
							))}
							<Button
								variant="text"
								onClick={() => setDialogOpen(true)}
								sx={{
									color: 'white',
									textDecoration: 'underline',
									p: 0,
									minWidth: 'auto',
									ml: '0.375rem',
									fontSize: 'inherit',
								}}
								data-testid="review_count_link"
							>
								{totalReviews} Reviews
							</Button>
						</Box>
					</Box>

					{/* Middle: rating distribution bars */}
					<Box sx={featuredProviderReviewsStyles.barsSection}>
						{sortedRatings.map((rating) => {
							const labelId = `rating-label-${rating.rating}`;
							const progressValue =
								totalReviews > 0
									? Math.min(100, Math.max(0, ((rating.reviewCount ?? 0) / totalReviews) * 100))
									: 0;
							return (
								<Box key={rating.rating} sx={featuredProviderReviewsStyles.ratingRow}>
									<Typography
										variant="bodyMediumBook"
										id={labelId}
										data-testid={`rating_label_${rating.rating}`}
										sx={{ color: 'white', minWidth: '0.75rem', textAlign: 'right' }}
									>
										{rating.rating}
									</Typography>
									<Box sx={featuredProviderReviewsStyles.ratingBar}>
										<BannerLinearProgress
											variant="determinate"
											value={progressValue}
											aria-labelledby={labelId}
										/>
									</Box>
								</Box>
							);
						})}
					</Box>

					{/* Right: action buttons */}
					<Box sx={featuredProviderReviewsStyles.buttonsSection}>
						{totalReviews > 1 && (
							<Button
								variant="outlined"
								onClick={() => setDialogOpen(true)}
								sx={bannerButtonStyles}
								data-testid="see_all_reviews_button"
							>
								See all reviews
							</Button>
						)}
						{/* Write a review button — hidden until Google Place ID is available
						<Button
							variant="outlined"
							{...(writeReviewUrl
								? { component: 'a', href: writeReviewUrl, target: '_blank', rel: 'noopener noreferrer' }
								: {})}
							sx={{
								...bannerButtonStyles,
								backgroundColor: 'white',
								color: '#0C3867',
								'&:hover': {
									...bannerButtonHoverStyles,
									backgroundColor: 'rgba(255, 255, 255, 0.9)',
								},
							}}
							data-testid="write_review_button"
						>
							Write a review
						</Button>
						*/}
					</Box>
				</Box>

				{/* Featured review cards */}
				<Box sx={featuredProviderReviewsStyles.reviewsContainer}>
					{featuredReviews.map((review) =>
						review.reviewId ? (
							<ReviewCard
								key={review.reviewId}
								review={review}
								expanded={expandedReviews[review.reviewId] ?? false}
								onToggle={() => toggleExpand(review.reviewId)}
							/>
						) : null
					)}
				</Box>

				{/* View all button */}
				{totalReviews > 1 && (
					<Box sx={featuredProviderReviewsStyles.viewAllContainer}>
						<Button
							variant="outlined"
							onClick={() => setDialogOpen(true)}
							sx={{ borderRadius: '2rem', px: '2rem' }}
							data-testid="view_all_button"
						>
							View all
						</Button>
					</Box>
				)}
			</Box>

			<Dialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				maxWidth="md"
				fullWidth
				data-testid="reviews_dialog"
			>
				<DialogTitle>{providerName}</DialogTitle>
				<DialogContent>
					{[...(reviews.reviews ?? [])]
						.filter((r) => r != null)
						.sort((a, b) => Number(b.rating ?? 0) - Number(a.rating ?? 0))
						.map((review) => (
							<Box key={review.reviewId} sx={featuredProviderReviewsStyles.reviewCard} mb={2}>
								<StarRow rating={Number(review.rating ?? 0)} reviewId={review.reviewId} />
								<Typography variant="bodyMediumBook">{review.comments}</Typography>
								<ReviewMeta source={review.source} timestamp={review.reviewTimestamp} />
							</Box>
						))}
				</DialogContent>
			</Dialog>
		</Box>
	);
}

function StarRow({ rating, reviewId }: { rating: number; reviewId: string }) {
	const starCount = Math.floor(rating);
	return (
		<Box
			component="span"
			aria-label={`${rating} star${rating === 1 ? '' : 's'}`}
			role="img"
			sx={featuredProviderReviewsStyles.starRow}
		>
			{Array.from({ length: starCount }).map((_, i) => (
				<StarIcon key={`${reviewId}-star-${i}`} sx={{ fontSize: 16, color: '#F5C518' }} />
			))}
		</Box>
	);
}

function ReviewCard({ review, expanded, onToggle }: { review: Review; expanded: boolean; onToggle: () => void }) {
	const fullText = review.comments ?? '';
	const shouldTruncate = fullText.length > 150;
	const displayText = !expanded && shouldTruncate ? fullText.slice(0, 150).trim() + '...' : fullText;

	return (
		<Box sx={featuredProviderReviewsStyles.reviewCard} data-testid="review_card">
			<StarRow rating={Number(review.rating ?? 0)} reviewId={review.reviewId} />
			<Typography variant="bodyMediumBook" data-testid="review_comment">
				{displayText}
				{shouldTruncate && !expanded && (
					<Button variant="text" size="small" onClick={onToggle} data-testid="read_more_button">
						Read more
					</Button>
				)}
			</Typography>
			<ReviewMeta source={review.source} timestamp={review.reviewTimestamp} />
		</Box>
	);
}

function ReviewMeta({ source, timestamp }: { source?: string | null; timestamp?: string | null }) {
	return (
		<Box sx={featuredProviderReviewsStyles.reviewMeta}>
			{source && (
				<Typography variant="bodyMediumBook" color="text.secondary" data-testid="review_source">
					Posted on {source}
				</Typography>
			)}
			{timestamp && !Number.isNaN(new Date(timestamp).getTime()) && (
				<Typography variant="bodyMediumBook" color="text.secondary" data-testid="review_timestamp">
					{new Date(timestamp).toLocaleDateString('en-US', {
						year: 'numeric',
						month: 'short',
						day: 'numeric',
						timeZone: 'UTC',
					})}
				</Typography>
			)}
		</Box>
	);
}
